import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Stack,
  alpha,
  CircularProgress,
  Skeleton,
  Theme
} from '@mui/material';

type Props = {
  corPrincipal: string;
  theme: Theme;
};

const LinkPagamentoLoading: React.FC<Props> = ({ corPrincipal, theme }) => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(145deg, ${alpha(corPrincipal, 0.05)} 0%, ${alpha(
          corPrincipal,
          0.02
        )} 100%)`,
        py: { xs: 3, md: 6 },
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '300px',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
            theme.palette.secondary.main,
            0.08
          )} 100%)`,
          clipPath: 'polygon(0 0, 100% 0, 100% 60%, 0 100%)',
          zIndex: 0
        }
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}
      >
        <Grid
          container
          spacing={{ xs: 2, md: 4 }}
          sx={{
            flex: 1,
            display: 'flex',
            '& > .MuiGrid-item': {
              display: 'flex'
            }
          }}
        >
          <Grid item xs={12} lg={8} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Stack spacing={3} sx={{ height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  p: { xs: 3, md: 4 },
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4
                  }}
                >
                  <Box>
                    <Skeleton variant="text" width={100} height={20} />
                    <Skeleton variant="text" width={200} height={60} />
                  </Box>
                  <Skeleton
                    variant="rectangular"
                    width={150}
                    height={80}
                    sx={{ borderRadius: 2 }}
                  />
                </Box>

                <Grid container spacing={5} alignItems="center">
                  <Grid item xs={12} md={5} display="flex" justifyContent="center">
                    <Skeleton
                      variant="rectangular"
                      width={220}
                      height={220}
                      sx={{ borderRadius: 4 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Skeleton variant="text" width={200} height={40} />
                    <Skeleton variant="text" width="100%" height={100} />
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={48}
                      sx={{ borderRadius: 3 }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="text" width={150} height={32} />
                </Stack>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={100}
                      sx={{ borderRadius: 3 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={100}
                      sx={{ borderRadius: 3 }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
            <Stack spacing={3} sx={{ width: '100%', height: '100%' }}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
                elevation={0}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="text" width={120} height={32} />
                </Stack>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    sx={{ borderRadius: 2 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    sx={{ borderRadius: 2 }}
                  />
                </Box>
              </Paper>

              <Paper
                sx={{
                  p: 3,
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
                elevation={0}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="text" width={80} height={32} />
                </Stack>
                <Stack spacing={2.5}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    sx={{ borderRadius: 2 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={80}
                    sx={{ borderRadius: 2 }}
                  />
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            width: '100%',
            py: 3
          }}
        >
          <Skeleton variant="text" width={200} height={24} />
        </Box>

        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.7),
            backdropFilter: 'blur(4px)',
            zIndex: 9999
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              border: `1px solid ${alpha(corPrincipal, 0.2)}`
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              sx={{
                color: corPrincipal,
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': {
                    opacity: 0.6
                  },
                  '50%': {
                    opacity: 1
                  },
                  '100%': {
                    opacity: 0.6
                  }
                }
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                animation: 'pulseText 1.5s ease-in-out infinite',
                '@keyframes pulseText': {
                  '0%': {
                    opacity: 0.5
                  },
                  '50%': {
                    opacity: 1
                  },
                  '100%': {
                    opacity: 0.5
                  }
                }
              }}
            >
              Carregando dados da cobrança...
            </Typography>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default LinkPagamentoLoading;
